import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Select,
    Space,
    Table,
    Tag,
    Typography,
    message
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import Layout from './Layout';
import {
    ACCESS_OPTIONS,
    tmApi
} from '../utils/taskManager';

const { Title, Text } = Typography;

const TaskManagerFieldPermissionsPage = () => {
    const [loading, setLoading] = useState(false);
    const [fields, setFields] = useState([]);
    const [permissions, setPermissions] = useState([]);
    const [roles, setRoles] = useState([]);
    const [roleId, setRoleId] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [metaRes, permRes] = await Promise.all([
                tmApi.get('/api/task-manager/meta'),
                tmApi.get(
                    '/api/task-manager/field-permissions'
                )
            ]);

            setIsAdmin(Boolean(metaRes.data?.isAdmin));
            setFields(permRes.data?.fields || []);
            setPermissions(permRes.data?.permissions || []);
            setRoles(permRes.data?.roles || []);

            if (
                !roleId &&
                (permRes.data?.roles || []).length
            ) {
                setRoleId(permRes.data.roles[0].id);
            }
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Ошибка загрузки прав'
            );
        } finally {
            setLoading(false);
        }
    }, [roleId]);

    useEffect(() => {
        load();
    }, [load]);

    const currentMap = useMemo(() => {
        const map = {};
        permissions.forEach(item => {
            if (
                item.subject_type === 'role' &&
                Number(item.subject_id) === Number(roleId)
            ) {
                map[item.field_definition_id] =
                    item.access_level;
            }
        });
        return map;
    }, [permissions, roleId]);

    const setAccess = async (fieldId, accessLevel) => {
        try {
            await tmApi.put(
                '/api/task-manager/field-permissions',
                {
                    field_definition_id: fieldId,
                    subject_type: 'role',
                    subject_id: roleId,
                    access_level: accessLevel
                }
            );
            message.success('Сохранено');
            load();
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Ошибка сохранения'
            );
        }
    };

    const setAll = async accessLevel => {
        const items = fields.map(field => ({
            field_definition_id: field.id,
            subject_type: 'role',
            subject_id: roleId,
            access_level: accessLevel
        }));

        try {
            await tmApi.put(
                '/api/task-manager/field-permissions/bulk',
                { items }
            );
            message.success('Пакетно обновлено');
            load();
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Ошибка пакетного сохранения'
            );
        }
    };

    if (!isAdmin && roles.length === 0 && !loading) {
        return (
            <Layout>
                <div style={{ padding: 16 }}>
                    <Alert
                        type="warning"
                        showIcon
                        message="Настройка прав доступна администратору"
                    />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div style={{ padding: 16 }}>
                <Space
                    style={{
                        width: '100%',
                        justifyContent: 'space-between',
                        marginBottom: 16
                    }}
                    wrap
                >
                    <div>
                        <Title level={3} style={{ margin: 0 }}>
                            Глобальные права на поля
                        </Title>
                        <Text type="secondary">
                            Базовый доступ по ролям: основные и
                            кастомные поля. Конструктор прав
                            уточняет по типу работ и стадии.
                        </Text>
                    </div>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={load}
                    >
                        Обновить
                    </Button>
                </Space>

                {!isAdmin ? (
                    <Alert
                        type="warning"
                        showIcon
                        style={{ marginBottom: 12 }}
                        message="Только администратор может менять права"
                    />
                ) : null}

                <Space style={{ marginBottom: 12 }} wrap>
                    <Select
                        style={{ width: 320 }}
                        placeholder="Роль"
                        value={roleId}
                        onChange={setRoleId}
                        options={roles.map(role => ({
                            value: role.id,
                            label: `${role.name} (#${role.id})`
                        }))}
                    />
                    <Button
                        disabled={!isAdmin || !roleId}
                        onClick={() => setAll('read')}
                    >
                        Всем чтение
                    </Button>
                    <Button
                        disabled={!isAdmin || !roleId}
                        onClick={() => setAll('edit')}
                    >
                        Всем редактирование
                    </Button>
                    <Button
                        disabled={!isAdmin || !roleId}
                        onClick={() => setAll('full')}
                    >
                        Всем полные
                    </Button>
                    <Button
                        danger
                        disabled={!isAdmin || !roleId}
                        onClick={() => setAll('none')}
                    >
                        Снять
                    </Button>
                </Space>

                <Table
                    rowKey="id"
                    loading={loading}
                    dataSource={fields}
                    pagination={{ pageSize: 50 }}
                    columns={[
                        {
                            title: 'Поле',
                            render: (_, row) => (
                                <div>
                                    <Space size={6}>
                                        <span>{row.human_name}</span>
                                        <Tag
                                            color={
                                                row.field_kind ===
                                                'main'
                                                    ? 'blue'
                                                    : 'default'
                                            }
                                        >
                                            {row.field_kind ===
                                            'main'
                                                ? 'основное'
                                                : 'кастом'}
                                        </Tag>
                                    </Space>
                                    <Text type="secondary">
                                        {row.system_name}
                                    </Text>
                                </div>
                            )
                        },
                        {
                            title: 'Текущий доступ',
                            width: 160,
                            render: (_, row) => {
                                const level =
                                    currentMap[row.id] ||
                                    'none';
                                const opt =
                                    ACCESS_OPTIONS.find(
                                        item =>
                                            item.value ===
                                            level
                                    );
                                return (
                                    <Tag color={opt?.color}>
                                        {opt?.label || level}
                                    </Tag>
                                );
                            }
                        },
                        {
                            title: 'Назначить',
                            width: 220,
                            render: (_, row) => (
                                <Select
                                    style={{ width: '100%' }}
                                    disabled={
                                        !isAdmin || !roleId
                                    }
                                    value={
                                        currentMap[row.id] ||
                                        'none'
                                    }
                                    options={ACCESS_OPTIONS}
                                    onChange={value =>
                                        setAccess(row.id, value)
                                    }
                                />
                            )
                        }
                    ]}
                />
            </div>
        </Layout>
    );
};

export default TaskManagerFieldPermissionsPage;
